/* eslint-disable @typescript-eslint/no-explicit-any */
import { handleResponse } from "./handleResponse.ts"

/**
 * Handles mutations on a given promise and returns a modified promise.
 *
 * @param {Promise<Response>} promise - The promise to handle mutations on, i.e. Promise returned from fetch.
 *
 * @return {Promise<T>} - The modified promise that resolves to the specified type T.
 */
export function mutationHandler<T>(promise: Promise<Response>): Promise<T>;
/**
 * Handles mutation requests by invoking a fetcher function.
 *
 * @param {Function} fetcher - The fetcher function that performs the mutation request.
 * @return {Function} - A function that invokes the fetcher function with the provided arguments and returns a Promise resolving to the result of the mutation.
 */
export function mutationHandler<
    T,
    F extends(...args: any[]) => Promise<Response>
>(fetcher: F): (...args: Parameters<F>) => Promise<T>

export function mutationHandler<T, F extends(...args: any[]) => Promise<Response>>(
    fetcherOrPromise: F | Promise<Response>
): Promise<T> | ((...args: Parameters<F>) => Promise<T>) {
    if (isPromise(fetcherOrPromise)) {
        return fetcherOrPromise.then((res) => handleResponse<T>(res))
    } else {
        return  (...args: Parameters<F>) =>
            fetcherOrPromise(...args).then((res) => handleResponse<T>(res))
    }
}

function isPromise(
    fnOrPromise: ((...args: any[]) => Promise<Response>) | Promise<Response>
): fnOrPromise is Promise<Response> {
    return fnOrPromise instanceof Promise
}

